import React, { useState } from "react";

import {
  AlertTriangle,
  ArrowLeftRight,
  Check,
  CheckCircle,
  Pencil,
  RotateCcw,
  ShieldOff,
  Wallet,
  X,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { ManualStatusChangeDialog } from "~/pages/purchase-orders/components/order-detail/ManualStatusChangeDialog";
import { ModifyDispatchOrderDialog } from "~/pages/purchase-orders/components/order-detail/ModifyDispatchOrderDialog";
import { SalesRequest, SalesRequestItem } from "~/types/client";
import {
  confirmApproveDocument,
  confirmForceApproveWithoutPayment,
  confirmMarkAsPaid,
  confirmMarkAsTest,
  confirmMarkAsUnpaid,
} from "~/utils/swalHelpers";

interface Payment {
  id: string;
  amount: number;
  payment_method: string;
  is_verified: boolean;
  [key: string]: unknown;
}

interface StatusActionButtonsProps {
  selectedOrder: SalesRequest;
  profileStore: { role: string; id: string };
  payments: Payment[];
  localItems: SalesRequestItem[];
  canApproveDocument: boolean;
  canMarkAsPaid: boolean;
  isAdmin: boolean;
  canModifyDispatchOrder: boolean;
  // Action handlers from useOrderActions
  markAsTest: () => void;
  markAsDelivered: () => void;
  revertToInTransit: () => void;
  markAsPaid: () => void;
  markAsUnpaid: () => void;
  handleForceApproveWithoutPaymentValidation: () => Promise<void>;
  // Sheet handlers
  setIsTransportistaRejectSheetOpen: (open: boolean) => void;
  // Callbacks
  onOrderStatusChange?: () => void;
  onCloseOrderDialog?: () => void;
}

export const StatusActionButtons: React.FC<StatusActionButtonsProps> = ({
  selectedOrder,
  profileStore,
  payments,
  localItems,
  canApproveDocument,
  canMarkAsPaid,
  isAdmin,
  canModifyDispatchOrder,
  markAsTest,
  markAsDelivered,
  revertToInTransit,
  markAsPaid,
  markAsUnpaid,
  handleForceApproveWithoutPaymentValidation,
  setIsTransportistaRejectSheetOpen,
  onOrderStatusChange,
  onCloseOrderDialog,
}) => {
  const [showModifyDispatchDialog, setShowModifyDispatchDialog] =
    useState(false);
  const [showManualStatusDialog, setShowManualStatusDialog] = useState(false);

  return (
    <>
      {/* Force Approve Without Payment Validation - Superadmin only, for prepaid orders with payments */}
      {selectedOrder.status === "prepaid" &&
        profileStore.role === "superadmin" &&
        payments?.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <Button
              onClick={async () => {
                const confirmed = await confirmForceApproveWithoutPayment();
                if (confirmed) {
                  await handleForceApproveWithoutPaymentValidation();
                }
              }}
              variant="outline"
              className="h-9 w-full justify-center gap-2"
              size="sm"
            >
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Aprobar Sin Validar Pagos
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">
              Solo usuarios super admin - No verifica pagos
            </p>
          </div>
        )}

      {/* Mark as Delivered Button - for superadmin users on in_transit orders */}
      {selectedOrder.status === "in_transit" &&
        profileStore.role === "superadmin" && (
          <div className="mt-4 border-t pt-4">
            <Button
              onClick={() => {
                markAsDelivered();
              }}
              className="h-9 w-full justify-center gap-2 bg-green-600 hover:bg-green-700"
              size="sm"
            >
              <Check className="h-4 w-4" />
              Marcar como Entregado
            </Button>
          </div>
        )}

      {/* Revert to In Transit Button - for superadmin users on delivered orders */}
      {selectedOrder.status === "delivered" &&
        profileStore.role === "superadmin" && (
          <div className="mt-4 border-t pt-4">
            <Button
              onClick={revertToInTransit}
              variant="outline"
              className="h-9 w-full justify-center gap-2"
              size="sm"
            >
              <RotateCcw className="h-4 w-4" />
              Revertir a En Tránsito
            </Button>
          </div>
        )}

      {/* Manual freeform status change - superadmin only, available in any status */}
      {profileStore.role === "superadmin" && (
        <div className="mt-4 border-t pt-4">
          <Button
            onClick={() => setShowManualStatusDialog(true)}
            variant="outline"
            className="h-9 w-full justify-center gap-2"
            size="sm"
          >
            <ArrowLeftRight className="h-4 w-4" />
            Cambiar Estado
          </Button>
          <p className="mt-1 text-xs text-muted-foreground">
            Solo superadmin · Cambio manual con motivo (queda en el historial)
          </p>
        </div>
      )}

      {/* Transportista Rejection Actions - for transportista and superadmin users on in_transit orders */}
      {(profileStore.role === "transportista" ||
        profileStore.role === "superadmin") &&
        selectedOrder.status === "in_transit" && (
          <div className="mt-4 border-t pt-4">
            <Button
              onClick={() => setIsTransportistaRejectSheetOpen(true)}
              variant="outline"
              className="h-9 w-full justify-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              size="sm"
            >
              <X className="h-4 w-4" />
              Rechazar Nota de Venta
            </Button>
          </div>
        )}

      {/* Document Approval Actions - for facturacion users */}
      {selectedOrder.status === "accepted" && canApproveDocument && (
        <div className="mt-4 border-t pt-4">
          <Button
            variant="outline"
            className="h-9 w-full justify-center gap-2"
            onClick={async () => {
              const confirmed = await confirmApproveDocument();
              if (confirmed) {
                try {
                  const { error } = await supabase
                    .from("sales_request")
                    .update({
                      status: "por_facturar",
                      updated_at: new Date().toISOString(),
                    })
                    .eq("id", selectedOrder.id);

                  if (error) throw error;

                  // Trigger logs the change; just attach the reason to that row.
                  await supabase.rpc("annotate_latest_status_change", {
                    p_sales_request_id: selectedOrder.id,
                    p_new_status: "por_facturar",
                    p_comment: "Documento aprobado para facturación",
                  });

                  // Create pick task for warehouse
                  const { data: pickTaskData, error: pickTaskError } =
                    await supabase.functions.invoke("create-pick-task", {
                      body: { salesRequestId: selectedOrder.id },
                    });

                  if (pickTaskError) {
                    console.error("Error creating pick task:", pickTaskError);
                    toast({
                      title: "Advertencia",
                      description:
                        "No se pudo crear la tarea de picking automaticamente. Contacte a soporte.",
                      variant: "destructive",
                    });
                  } else if (pickTaskData?.error) {
                    console.error(
                      "Pick task creation failed:",
                      pickTaskData.error
                    );
                    toast({
                      title: "Advertencia",
                      description: pickTaskData.message || pickTaskData.error,
                      variant: "destructive",
                    });
                  } else {
                    console.log("Pick task created:", pickTaskData);
                  }

                  toast({
                    title: "Documento Aprobado",
                    description:
                      "La nota de venta ha sido enviada a Por Facturar.",
                    variant: "default",
                  });

                  if (onOrderStatusChange) await onOrderStatusChange();
                  // Return to the board so the card animates into Por Facturar.
                  onCloseOrderDialog?.();
                } catch (error) {
                  console.error("Error approving document:", error);
                  toast({
                    title: "Error",
                    description: "No se pudo aprobar el documento.",
                    variant: "destructive",
                  });
                }
              }
            }}
          >
            <CheckCircle className="h-4 w-4 text-green-600" />
            Aprobar Documento
          </Button>
        </div>
      )}

      {/* Admin actions (always at the bottom) */}
      {isAdmin && (
        <div className="mt-6 space-y-3">
          {/* Modify Dispatch Order button - only for dispatch_ready status */}
          {canModifyDispatchOrder && (
            <Button
              variant="outline"
              className="h-9 w-full justify-center gap-2"
              onClick={() => setShowModifyDispatchDialog(true)}
            >
              <Pencil className="h-4 w-4" />
              Modificar Productos
            </Button>
          )}

          {/* Mark as Test button for admins */}
          {selectedOrder.status !== "test" && (
            <Button
              variant="outline"
              className="h-9 w-full justify-center gap-2"
              onClick={async () => {
                const confirmed = await confirmMarkAsTest();
                if (confirmed) {
                  markAsTest();
                }
              }}
            >
              Marcar como Test
            </Button>
          )}
        </div>
      )}

      {/* Mark as Paid button - accessible to cobranza, admin, and superadmin */}
      {!selectedOrder.paid && canMarkAsPaid && (
        <div className="mt-6">
          <Button
            variant="outline"
            className="h-9 w-full justify-center gap-2"
            onClick={async () => {
              const confirmed = await confirmMarkAsPaid();
              if (confirmed) {
                markAsPaid();
              }
            }}
          >
            <Wallet className="h-4 w-4 text-green-600" />
            Marcar como Pagado
          </Button>
        </div>
      )}

      {/* Mark as Unpaid button - accessible to cobranza and superadmin only */}
      {selectedOrder.paid &&
        (profileStore.role === "superadmin" ||
          profileStore.role === "cobranza") && (
          <div className="mt-6">
            <Button
              variant="outline"
              className="h-9 w-full justify-center gap-2"
              onClick={async () => {
                const confirmed = await confirmMarkAsUnpaid();
                if (confirmed) {
                  markAsUnpaid();
                }
              }}
            >
              <ShieldOff className="h-4 w-4 text-red-600" />
              Marcar como NO Pagado
            </Button>
          </div>
        )}

      {/* Manual status change dialog (superadmin only) */}
      {profileStore.role === "superadmin" && (
        <ManualStatusChangeDialog
          open={showManualStatusDialog}
          onOpenChange={setShowManualStatusDialog}
          salesRequestId={selectedOrder.id}
          currentStatus={
            selectedOrder.status === "in_transit" &&
            !selectedOrder.assigned_transportista
              ? "facturado"
              : selectedOrder.status
          }
          onChanged={() => {
            if (onOrderStatusChange) onOrderStatusChange();
            // Close the whole order dialog so we return to the board (and see
            // the card animate into its new column).
            onCloseOrderDialog?.();
          }}
        />
      )}

      {/* ModifyDispatchOrderDialog */}
      {showModifyDispatchDialog && (
        <ModifyDispatchOrderDialog
          isOpen={showModifyDispatchDialog}
          onClose={() => setShowModifyDispatchDialog(false)}
          order={selectedOrder}
          currentItems={localItems}
          onOrderModified={() => {
            if (onOrderStatusChange) onOrderStatusChange();
            setShowModifyDispatchDialog(false);
          }}
        />
      )}
    </>
  );
};
